import "./AssetCollectionModal.scss"
import IconTrashBin from "assets/img/icon-trash-bin.svg?react"
import pictogramArticle from "assets/img/pictogram/article.svg"

import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import ConnectedAsset from "models/ConnectedAsset"
import { Guid } from "@/types"
import toastMessages from "stores/toastMessages"
import Lmid from "components/Lmid/Lmid"
import Button from "components/Button/Button"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import { Grid, GridItem } from "components/Grid"
import Text from "components/Text/Text"
import { Flex } from "components/Flex"
import AssetArticle from "models/AssetArticle"
import ArticleService from "services/ArticleService"
import SelectSearch from "components/Select/SelectSearch"
import confirmModals from "stores/confirmModals"
import SelectSearchOption from "components/Select/SelectSearchOption"
import assetSidebars from "stores/assetSidebars"
import { getRandomNumber } from "utils/utils"

interface IAssetCollectionModalProps {
    assetId: Guid
    connectedAssets: ConnectedAsset[]
    onClose: () => void
    onSave: (assetIds: Guid[]) => Promise<void>
}

const AssetCollectionModal: FC<IAssetCollectionModalProps> = ({
    assetId,
    connectedAssets,
    onClose,
    onSave,
}) => {
    const { t } = useTranslation()
    const [isSaving, setIsSaving] = useState(false)
    const [assets, setAssets] = useState<ConnectedAsset[]>(connectedAssets)
    const [assetToAdd, setAssetToAdd] = useState<AssetArticle | null>(null)
    const inititalAssetIds = useMemo(() => connectedAssets.map(a => a.assetId), [connectedAssets])
    const assetIds = useMemo(() => [assetId, ...assets.map(a => a.assetId)], [assetId, assets])
    const [assetSelectKey, setAssetSelectKey] = useState(getRandomNumber())
    const [forceKeepSearchOpen, setForceKeepSearchOpen] = useState(false)

    function onAssetAddClick() {
        if (!assetToAdd) return
        if (assetToAdd.linkedToAssetCollection && !inititalAssetIds.includes(assetToAdd.assetId)) {
            confirmModals.show(({
                title: t("common.attention"),
                children: t("asset.assetIsPartOfAnotherCollection"),
                confirmButtonLabel: t("common.add"),
                maxWidth: "586px",
                onConfirm: close => {
                    close?.()
                    handleAssetAdd()
                }
            }))
            return
        }
        handleAssetAdd()
    }

    function handleAssetAdd() {
        if (!assetToAdd) return
        const newAssets = [...assets]
        newAssets.push(new ConnectedAsset({
            assetId: assetToAdd.assetId,
            lmid: assetToAdd.lmid,
            labeled: assetToAdd.isLabeled,
            articleId: assetToAdd.articleId,
            articleDescription: assetToAdd.articleDescription || "",
            altArticleDescription: assetToAdd.altArticleDescription,
        }))
        setAssets(newAssets)
        setAssetToAdd(null)
        setAssetSelectKey(getRandomNumber())
    }

    function handleAssetDelete(index: number) {
        const newAssets = [...assets]
        newAssets.splice(index, 1)
        setAssets(newAssets)
    }

    async function handleSave() {
        setIsSaving(true)
        try {
            await onSave(assets.map(a => a.assetId))
            onClose()
        } catch (e) {
            console.error(e)
            toastMessages.showSystemError()
            setIsSaving(false)
        }
    }

    function renderAssetSelectValue(asset: AssetArticle) {
        return asset.lmid
    }

    function onShowAssetDetailsClick(clickedAssetId: Guid) {
        setForceKeepSearchOpen(true)
        assetSidebars.show({
            assetId: clickedAssetId,
            onClose: () => {
                setForceKeepSearchOpen(false)
            }
        })
    }

    function renderAssetSelectOption(asset: AssetArticle) {
        return (
            <SelectSearchOption
                title={
                    <Lmid
                        lmid={asset.lmid}
                        labeled={asset.isLabeled}
                        inCollection={asset.linkedToAssetCollection}
                    />
                }
                subtitle={`${t("common.artNo")}: ${asset.articleNo} | ${asset.altArticleDescription || asset.articleDescription}`}
                thumbnailUrl={asset.thumbnailUri}
                pictogramUrl={pictogramArticle}
                onShowDetailsClick={() => onShowAssetDetailsClick(asset.assetId)}
            />
        )
    }

    async function getAssets(search?: string) {
        return ArticleService.getAssetArticles({ criteria: search }, { excludeAssetIds: assetIds })
    }

    return (
        <ConfirmModal
            title={t("asset.manageCollection")}
            onClose={onClose}
            maxWidth="768px"
            onConfirm={handleSave}
            confirmButtonLabel={t("common.save")}
            isBusy={isSaving}>
            <Flex flow="column" className="h-100 overflow-hidden">
                <Grid columns="20rem minmax(0, auto)" alignItems="center" className="asset-collection-modal__table-header">
                    <Text type="pSmallest" className="font-bold p-1-5">
                        {t("common.lmid")}
                    </Text>
                    <Text type="pSmallest" className="font-bold p-1-5">
                        {t("common.article")}
                    </Text>
                </Grid>
                <Flex flow="column" className="asset-collection-modal__table-body h-100 overflow-hidden">
                    <Grid columns="32rem minmax(0, auto)" alignItems="center" gap="2.4rem" className="p-1-5">
                        <SelectSearch
                            key={assetSelectKey}
                            getOptions={search => Promise.all([getAssets(search)])}
                            onChange={value => setAssetToAdd(value || null)}
                            optionTemplate={renderAssetSelectOption}
                            valueTemplate={renderAssetSelectValue}
                            shouldShowUncontrolledValue
                            placeholder={t('common.pleaseSelect')}
                            forceKeepMenuOpen={forceKeepSearchOpen}
                            menuWidth="64.4rem"
                            onMenuOpen={() => setAssetToAdd(null)}
                            keepValueOnSelect
                            isInModal
                        />
                        <GridItem>
                            <Button
                                theme="secondary"
                                label={t('common.add')}
                                disabled={!assetToAdd}
                                onClick={onAssetAddClick}
                            />
                        </GridItem>
                    </Grid>
                    {assets.length > 0 &&
                        <div className="h-100 overflow-auto border-top-grey-100">
                            {assets.map((asset, assetIndex) => (
                                <Grid key={asset.assetId}
                                    columns="20rem minmax(0, auto) max-content"
                                    alignItems="center"
                                    className="border-grey-100-between">
                                    <GridItem className="p-1-5">
                                        <Lmid
                                            assetId={asset.assetId}
                                            lmid={asset.lmid}
                                            labeled={asset.labeled}
                                        />
                                    </GridItem>
                                    <GridItem className="p-1-5">
                                        <ArticleDescription
                                            articleId={asset.articleId}
                                            articleDescription={asset.altArticleDescription || asset.articleDescription}
                                        />
                                    </GridItem>
                                    <GridItem className="p-1-5">
                                        <Button
                                            theme="grey"
                                            round
                                            icon={<IconTrashBin />}
                                            ariaLabel={t("common.delete")}
                                            onClick={() => handleAssetDelete(assetIndex)}
                                        />
                                    </GridItem>
                                </Grid>
                            ))}
                        </div>
                    }
                </Flex>
            </Flex>
        </ConfirmModal>
    )
}

export default AssetCollectionModal
